"use client";

import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { LogOut, Menu, Settings, User, X } from "lucide-react";

const navigation = [
  { name: "Calculate", href: "/salary-calculator" },
  { name: "PAYE", href: "/paye-calculator" },
  { name: "Payslips", href: "/payslip-generator" },
  { name: "Bulk Payroll", href: "/bulk-payroll" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

export function WebsiteNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout, isLoading, isAdmin } = useAuth();

  const handleLogout = async () => {
    await logout();
    router.push("/");
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-30 bg-[#F3F6FA]/95 backdrop-blur">
      {/* Utility bar */}
      <div className="border-b border-[#DCE3EA]">
        <div className="mx-auto flex h-8 max-w-[1440px] items-center justify-between px-5 text-[10px] uppercase tracking-[.15em] text-slate-500 sm:px-8 lg:px-12">
          <span className="mono">Kenya payroll infrastructure</span>
          <div className="hidden items-center gap-5 sm:flex">
            <span>2026 tax tables</span>
            <Link href="/salary-calculator" className="quiet-link hover:underline">Free calculators</Link>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="border-b border-[#DCE3EA]">
        <div className="mx-auto grid h-[70px] max-w-[1440px] grid-cols-[1fr_auto] items-center px-5 sm:px-8 lg:grid-cols-[240px_1fr_auto] lg:px-12">
          <Link href="/" className="justify-self-start text-left">
            <div className="wordmark text-slate-950">SekaniPay</div>
            <div className="mono mt-1 text-[9px] tracking-[.12em] text-slate-400">PAYROLL / PEOPLE / KE</div>
          </Link>

          <nav className="hidden h-full items-stretch justify-self-start lg:flex">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`flex items-center border-l border-[#DCE3EA] px-6 text-[13px] transition-colors ${
                  pathname === item.href
                    ? "bg-white/60 text-slate-950 font-medium"
                    : "text-slate-600 hover:bg-white/60 hover:text-slate-950"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="border-l border-[#DCE3EA]" />
          </nav>

          <div className="flex items-center justify-self-end gap-5">
            {!isLoading && user && (
              <>
                {isAdmin && (
                  <Link
                    href="/admin/config"
                    className="hidden text-[12px] font-medium text-slate-600 underline decoration-[#B8B6AE] quiet-link hover:text-slate-950 sm:block"
                  >
                    Admin
                  </Link>
                )}
                <span className="hidden text-[12px] text-slate-500 sm:block">{user.fullName}</span>
                <button
                  onClick={handleLogout}
                  className="hidden text-[12px] font-medium text-slate-600 underline decoration-[#B8B6AE] quiet-link hover:text-slate-950 sm:block"
                >
                  Sign out
                </button>
              </>
            )}
            <Link
              href="/bulk-payroll"
              className="hidden ink-button border border-slate-950 bg-slate-950 px-4 py-2.5 text-[12px] font-medium text-white transition hover:border-[#2348C7] hover:bg-[#2348C7] sm:block"
            >
              Run payroll
            </Link>
            <button
              className="lg:hidden p-2 text-slate-600 hover:text-slate-950"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="border-b border-[#DCE3EA] bg-white px-5 py-4 lg:hidden animate-fade-up">
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`block border-b border-[#DCE3EA] py-3 text-[13px] ${
                  pathname === item.href
                    ? "font-medium text-slate-950"
                    : "text-slate-600"
                }`}
              >
                {item.name}
              </Link>
            ))}
            {!isLoading && user && (
              <div className="pt-3">
                <div className="flex items-center gap-2 py-2 text-[12px] text-slate-500">
                  <User className="h-3.5 w-3.5" />
                  {user.fullName}
                </div>
                {isAdmin && (
                  <Link href="/admin/config" className="flex items-center gap-2 py-2 text-[12px] text-[#2348C7]">
                    <Settings className="h-3.5 w-3.5" />
                    Admin Panel
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 py-2 text-[12px] text-slate-600"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}